/**
 * Where the three arrival codes live between the landing page and register().
 *
 * The Max landing page on privacypal.ai hands off to portal /signup carrying
 * ?promo=, ?ref= and ?trial=. Those codes have to survive:
 *
 *   - the multi-step wizard (identity -> verify-code -> plan),
 *   - a refresh or a back button,
 *   - the round trip to Google or Microsoft, which leaves the origin
 *     entirely and comes back to /auth/callback with nothing of ours on the
 *     query string,
 *   - and the strip that clears them out of the address bar, so a customer
 *     who copies the URL out of the bar and sends it to a colleague does not
 *     silently re-attribute that colleague to the same partner.
 *
 * sessionStorage, not localStorage, and not component state. Component state
 * dies on the SSO hop. localStorage would outlive the signup and credit a
 * partner for an unrelated account created weeks later on the same machine.
 * sessionStorage is exactly the lifetime of this signup attempt.
 *
 * Capture happens the moment /signup mounts, BEFORE anything can navigate, so
 * the codes are on disk before the browser has a chance to leave.
 *
 * Plain functions with an injectable storage so the persistence rules are
 * testable without a DOM, matching lib/family-portal's onboarding-state.js.
 */
import { mergeArrivalCodes, readCodesFromSearch, isEmptyCodes, NO_CODES } from './codes.ts';
import type { ArrivalCodes } from './codes.ts';

export const STORAGE_KEY = 'pp_max_signup_codes';

type MaybeStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> | null;

function defaultStorage(): MaybeStorage {
  try {
    return globalThis.sessionStorage || null;
  } catch {
    // Some browsers throw on the accessor itself when storage is blocked.
    return null;
  }
}

export function loadCodes(storage: MaybeStorage = defaultStorage()): ArrivalCodes {
  try {
    const raw = storage?.getItem(STORAGE_KEY);
    if (!raw) return { ...NO_CODES };
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return { ...NO_CODES };
    // Never trust what came back: a stale or hand-edited blob is merged
    // through the same normalization as a fresh arrival.
    return mergeArrivalCodes(null, parsed as Partial<ArrivalCodes>);
  } catch {
    return { ...NO_CODES };
  }
}

export function saveCodes(codes: ArrivalCodes, storage: MaybeStorage = defaultStorage()): ArrivalCodes {
  try {
    storage?.setItem(STORAGE_KEY, JSON.stringify(codes));
  } catch {
    // Private browsing or a full quota. The wizard keeps working in memory;
    // only the SSO round trip loses the codes, and that is better than
    // failing the signup.
  }
  return codes;
}

export function clearCodes(storage: MaybeStorage = defaultStorage()): void {
  try {
    storage?.removeItem(STORAGE_KEY);
  } catch {
    // Nothing to do: there was nothing to clear.
  }
}

/**
 * Read the codes off a query string, merge them over whatever is stored
 * (last touch wins per code) and persist the result.
 *
 * Called on mount with window.location.search. Writing even when the arrival
 * is empty is deliberate: it normalizes a blob written by an older build.
 */
export function captureArrivalCodes(
  search?: string,
  storage: MaybeStorage = defaultStorage(),
): ArrivalCodes {
  const arrived = readCodesFromSearch(search);
  const merged = mergeArrivalCodes(loadCodes(storage), arrived);
  if (isEmptyCodes(merged)) {
    // Nothing to remember. Leave storage alone rather than writing an empty
    // record that would read as "this signup was checked and had no codes".
    return merged;
  }
  return saveCodes(merged, storage);
}

/** The three codes shaped for POST /api/user/register. Empty codes are omitted. */
export function registerPayloadCodes(codes: ArrivalCodes): {
  promoCode?: string;
  referralCode?: string;
  trialCode?: string;
} {
  const payload: { promoCode?: string; referralCode?: string; trialCode?: string } = {};
  if (codes.promoCode) payload.promoCode = codes.promoCode;
  if (codes.referralCode) payload.referralCode = codes.referralCode;
  if (codes.trialCode) payload.trialCode = codes.trialCode;
  return payload;
}
